import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveTokens } from '@/theme/theme';

const TITLE_ID = 'paybacks-help-title';

/** One row per action the pair card offers: its label, then what it does (TMMAPP-0121 item 1). */
const ROWS = [
  { label: 'reimb.yes', body: 'reimb.help.yes' },
  { label: 'reimb.no', body: 'reimb.help.no' },
  { label: 'reimb.later', body: 'reimb.help.later' },
  // TMMAPP-0119 item 25: the missing-account offer belongs to Mark as transfer.
  {
    label: 'reimb.help.markTransfer',
    body: 'reimb.help.markTransferBody',
    extra: 'reimb.help.markTransferMissing',
  },
  { label: 'reimb.help.pickAnother', body: 'reimb.help.pickAnotherBody' },
] as const;

/**
 * "How paybacks work" (TMMAPP-0121 item 1): a read-only dialog, sm wide and full screen below `sm`
 * (G11). The intro, a two-column list of the five actions (stacked at `xs`), and a footnote that no
 * AI is used (MM-D-95: the matches are arithmetic). "Got it" is the only action; MUI's focus trap
 * returns focus to "Explain this" on close.
 */
export function PaybacksHelpDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle id={TITLE_ID}>{t('reimb.help.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('reimb.help.intro')}
        </Typography>
        <Box
          component="dl"
          data-paybacks-help-rows=""
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 3fr)' },
            columnGap: 2,
            rowGap: { xs: 0.5, sm: 1.5 },
          }}
        >
          {ROWS.map((row) => (
            <Box key={row.label} sx={{ display: 'contents' }}>
              <Typography
                component="dt"
                variant="body2"
                sx={{ fontWeight: 500, mt: { xs: 1, sm: 0 } }}
              >
                {t(row.label)}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, color: tokens.text2 }}>
                {t(row.body)}
                {'extra' in row && ` ${t(row.extra)}`}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" component="p" sx={{ mt: 2, color: tokens.text2 }}>
          {t('reimb.help.footnote')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t('reimb.help.gotIt')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
